import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { ConversationEntry, EntryFeedback, updateEntryFeedback } from '../store/conversationSlice';
import { ConversationCategory } from '../types/conversation';
import FeedbackPopover from './FeedbackPopover';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';
import { formatClockTime } from '../utils/formatClockTime';
import EarlierTag from './EarlierTag';
import { useIsWatcher } from '../contexts/SessionViewerContext';
import {
  STRIP_DROPDOWN_CLASS,
  STRIP_FOOTER_CLASS,
  STRIP_HEADER_CLASS,
  STRIP_PILL_CLASS,
  STRIP_PREVIEW_CLASS,
  STRIP_TITLE_GROUP_CLASS,
  StripChevron,
  StripEmptyState,
  StripRule,
} from './summaryStrip';

const formatAnswer = (answer: string): React.ReactNode => {
  const lines = answer.split('\n');
  return (
    <div>
      {lines.map((line, index) => {
        if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
          return (
            <li key={index} className="ml-4 mb-0.5 text-[11px] text-ink list-disc">
              {line.trim().substring(1).trim()}
            </li>
          );
        }
        if (line.trim()) {
          return (
            <p key={index} className="mb-1 text-[11px] text-ink">
              {line.trim()}
            </p>
          );
        }
        return null;
      }).filter(Boolean)}
    </div>
  );
};

const STATUS_DOT_COLOR: Record<string, string> = {
  completed: 'bg-success',
  pending: 'bg-warning',
  error: 'bg-error',
};

interface EntryCardProps {
  entry: ConversationEntry;
}

const InquiryCard: React.FC<EntryCardProps> = ({ entry }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  // Feedback writes to the owner's session: a watcher only reads the card.
  const isWatcher = useIsWatcher();
  const isPending = entry.status === 'pending';

  const handleFeedbackSubmit = async (rating: 'up' | 'down', reason: string) => {
    const roomId = sessionManager.getRoomId();

    const feedback: EntryFeedback = {
      rating,
      reason: reason || undefined,
      submittedAt: new Date().toISOString(),
    };
    dispatch(updateEntryFeedback({ id: entry.id, feedback }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'conversation',
        entryTimestamp: entry.timestamp,
        category: entry.category,
        rating,
        reason,
      });
    } catch (err) {
      console.warn('[NewInquiryBar] Entry feedback persist failed:', err);
    }
  };

  // The macOS card: white, radius 8, a 1.5 signature border; header on paper,
  // the question on signature-tint, the answer on surface-subtle, hairlines between.
  return (
    <div className="overflow-hidden rounded-control border-[1.5px] border-signature bg-surface">
      {/* Card header */}
      <div className="flex items-center gap-1.5 border-b border-hairline bg-paper px-2 py-1.5">
        <span className="text-[10px] font-bold uppercase text-signature">New Inquiry</span>
        <span className="text-[10px] text-ink-muted">{formatClockTime(entry.timestamp)}</span>
        {entry.isRestored && <EarlierTag />}
        <span className="flex-1" />
        {!isWatcher && (
          <FeedbackPopover
            currentFeedback={entry.feedback}
            onSubmit={handleFeedbackSubmit}
            disabled={isPending}
          />
        )}
        <div
          className={`h-2 w-2 flex-shrink-0 rounded-full ${STATUS_DOT_COLOR[entry.status] ?? 'bg-ink-muted'}`}
          title={entry.status}
        />
      </div>

      {/* Question */}
      <div className="space-y-[3px] border-b border-hairline bg-signature-tint p-2">
        <p className="text-[10px] font-bold uppercase text-ink">Customer asked</p>
        <p className="text-xs font-medium leading-snug text-ink">{entry.question}</p>
      </div>

      {/* Answer section */}
      <div className="space-y-[3px] bg-surface-subtle p-2">
        <p className="text-[10px] font-bold uppercase text-ink">AI Generated Answer</p>
        {isPending && !entry.answer ? (
          <div className="flex items-center gap-1.5 text-[11px] text-ink-muted">
            <svg className="animate-spin w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            Generating AI answer...
          </div>
        ) : entry.answer ? (
          formatAnswer(entry.answer)
        ) : (
          <p className="text-[11px] text-ink-muted italic">No AI answer available</p>
        )}
      </div>
    </div>
  );
};

export const NewInquiryBar: React.FC = () => {
  const { entries } = useSelector((state: RootState) => state.conversation);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);
  const seenCountRef = useRef(0);

  const newInquiries = entries.filter(
    (e) => e.category === ConversationCategory.NEW_INQUIRY
  );
  const latestInquiry = newInquiries.length > 0 ? newInquiries[newInquiries.length - 1] : null;
  // Only inquiries that arrived live count as new: restored history is silent.
  const liveInquiryCount = newInquiries.filter((e) => !e.isRestored).length;

  // Badge flashes for 3 pulses (3 × 0.4s = 1.2s) then stays red until user opens.
  useEffect(() => {
    if (!isExpanded && liveInquiryCount > seenCountRef.current) {
      setHasNew(true);
      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [liveInquiryCount, isExpanded]);

  useEffect(() => {
    if (isExpanded && feedRef.current) {
      requestAnimationFrame(() => {
        if (feedRef.current) {
          feedRef.current.scrollTop = feedRef.current.scrollHeight;
        }
      });
    }
  }, [newInquiries.length, isExpanded]);

  const handleToggle = () => {
    setIsExpanded((prev) => {
      const opening = !prev;
      if (opening) {
        seenCountRef.current = liveInquiryCount;
        setHasNew(false);
      }
      return opening;
    });
  };

  const isHighlighted = !isExpanded && hasNew;

  return (
    <>
      {/* Collapsed bar */}
      <div
        className={`${STRIP_HEADER_CLASS} ${
          isHighlighted ? 'bg-signature-tint hover:bg-surface-sunken' : 'hover:bg-surface-subtle'
        }`}
        onClick={handleToggle}
      >
        <div className={STRIP_TITLE_GROUP_CLASS}>
          <h3 className="text-xs font-semibold text-ink">New Inquiries</h3>
          <span className={`${STRIP_PILL_CLASS} ${
            isHighlighted
              ? `bg-error text-white${isFlashing ? ' animate-badge-flash' : ''}`
              : newInquiries.length > 0
              ? 'bg-signature-tint text-signature'
              : 'bg-paper text-ink-secondary'
          }`}>
            {newInquiries.length}
          </span>
        </div>

        <StripRule />

        {latestInquiry ? (
          <div className={STRIP_PREVIEW_CLASS}>
            <span className="flex-shrink-0 font-medium text-ink-secondary">Latest:</span>
            <span className="truncate text-ink-muted">
              {latestInquiry.question.length > 60
                ? `${latestInquiry.question.substring(0, 60)}\u2026`
                : latestInquiry.question}
            </span>
          </div>
        ) : (
          <span className="min-w-0 flex-1 truncate text-[11px] text-ink-muted">No new inquiries yet</span>
        )}

        <button
          className="flex-shrink-0 p-0.5 text-ink-muted transition-colors hover:text-ink-secondary"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          <StripChevron isExpanded={isExpanded} />
        </button>
      </div>

      {/* Expanded dropdown — contained within this bar's own relative column */}
      {isExpanded && (
        <div className={STRIP_DROPDOWN_CLASS}>
          <div
            ref={feedRef}
            className="max-h-accordion overflow-y-auto bg-paper p-2.5"
            style={{ scrollBehavior: 'smooth' }}
          >
            {newInquiries.length === 0 ? (
              <StripEmptyState title="No new inquiries yet" detail="Unrecognized questions will appear here" />
            ) : (
              <div className="space-y-2.5">
                {newInquiries.map((entry) => (
                  <InquiryCard key={entry.id} entry={entry} />
                ))}
                <div className="h-1" />
              </div>
            )}
          </div>

          {newInquiries.length > 0 && (
            <div className={STRIP_FOOTER_CLASS}>
              {newInquiries.length} new inquir{newInquiries.length === 1 ? 'y' : 'ies'} detected
            </div>
          )}
        </div>
      )}
    </>
  );
};
